/* Draws the 1080x1080 shareable coach card on a canvas from live Passport data. */
const SIZE = 1080;
const DARK = '#0a0a0a';
const TEAL = '#00d4aa';
const RED = '#e63946';
const LINE = '#2a2a2a';
const MUTED = '#92959f';
const AVATAR_TONES = ['#123b33', '#1d2f45', '#3a2130', '#2a2f1c'];

export const loadImage = src => new Promise(resolve => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => resolve(null);
  image.src = src;
});

export const initials = name => (name || '').trim().split(/\s+/).map(part => part[0]).slice(0, 2).join('').toUpperCase() || 'SHC';

export const wrap = (ctx, text, maxWidth) => {
  const lines = [];
  let line = '';
  (text || '').split(/\s+/).forEach(word => {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line) { lines.push(line); line = word; }
    else line = next;
  });
  if (line) lines.push(line);
  return lines;
};

export const fitFont = (ctx, text, weight, start, min, family, maxWidth) => {
  let size = start;
  do {
    ctx.font = `${weight} ${size}px ${family}`;
    if (ctx.measureText(text).width <= maxWidth) break;
    size -= 2;
  } while (size > min);
  return size;
};

export const drawPin = (ctx, x, y, color) => {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y - 6, 9, Math.PI, 0);
  ctx.lineTo(x, y + 12);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = DARK;
  ctx.beginPath();
  ctx.arc(x, y - 6, 3.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

export const drawShareCard = async ({ passport, url, qrCanvas, logoSrc, photoSrc, sport = 'BASEBALL' }) => {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (document.fonts?.ready) { try { await document.fonts.ready; } catch { /* fonts are optional */ } }
  const [logo, photo] = await Promise.all([logoSrc ? loadImage(logoSrc) : null, photoSrc ? loadImage(photoSrc) : null]);

  ctx.fillStyle = DARK;
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, SIZE - 2, SIZE - 2);

  if (logo) {
    const height = 62;
    ctx.drawImage(logo, 72, 66, (logo.width / logo.height) * height, height);
  } else {
    ctx.fillStyle = RED;
    ctx.font = '700 44px "Barlow Condensed", sans-serif';
    ctx.textBaseline = 'top';
    ctx.fillText('SHC', 72, 70);
  }
  ctx.textBaseline = 'top';

  const cx = 248;
  const cy = 330;
  const radius = 134;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (photo) {
    const scale = Math.max((radius * 2) / photo.width, (radius * 2) / photo.height);
    const w = photo.width * scale;
    const h = photo.height * scale;
    ctx.drawImage(photo, cx - w / 2, cy - radius, w, h);
  } else {
    ctx.fillStyle = AVATAR_TONES[initials(passport.name).charCodeAt(0) % AVATAR_TONES.length];
    ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
    ctx.fillStyle = TEAL;
    ctx.font = '700 118px "Barlow Condensed", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials(passport.name), cx, cy + 6);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
  }
  ctx.restore();
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, cy, radius + 6, 0, Math.PI * 2);
  ctx.stroke();

  const textX = 438;
  const textWidth = SIZE - textX - 72;
  const name = passport.name || 'Your name';
  const nameSize = fitFont(ctx, name, 700, 76, 40, '"Barlow Condensed", sans-serif', textWidth);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(name, textX, 236);
  const nameWidth = Math.min(ctx.measureText(name).width, textWidth);
  ctx.fillStyle = RED;
  ctx.fillRect(textX, 236 + nameSize + 14, nameWidth, 6);

  let y = 236 + nameSize + 44;
  ctx.fillStyle = TEAL;
  ctx.font = '600 30px "Barlow Condensed", sans-serif';
  wrap(ctx, passport.title || 'Baseball Development Coach', textWidth).slice(0, 2).forEach(line => {
    ctx.fillText(line, textX, y);
    y += 38;
  });
  if (passport.location) {
    ctx.font = '400 26px "Barlow Condensed", sans-serif';
    drawPin(ctx, textX + 9, y + 15, MUTED);
    ctx.fillStyle = MUTED;
    ctx.fillText(passport.location, textX + 30, y + 2);
  }

  ctx.strokeStyle = LINE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(72, 570);
  ctx.lineTo(SIZE - 72, 570);
  ctx.stroke();

  const qrSize = 312;
  const qrX = 72;
  const qrY = 646;
  ctx.fillStyle = '#101010';
  ctx.fillRect(qrX - 14, qrY - 14, qrSize + 28, qrSize + 28);
  ctx.strokeStyle = LINE;
  ctx.strokeRect(qrX - 14, qrY - 14, qrSize + 28, qrSize + 28);
  if (qrCanvas) ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

  const ctaX = qrX + qrSize + 66;
  const ctaWidth = SIZE - ctaX - 72;
  ctx.fillStyle = '#ffffff';
  ctx.font = '700 40px "Barlow Condensed", sans-serif';
  let ctaY = qrY + 26;
  wrap(ctx, 'Scan to view my coaching profile and connect your athlete', ctaWidth).forEach(line => {
    ctx.fillText(line, ctaX, ctaY);
    ctaY += 50;
  });
  ctx.fillStyle = MUTED;
  const urlSize = fitFont(ctx, url, 400, 22, 13, '"JetBrains Mono", monospace', ctaWidth);
  ctx.font = `400 ${urlSize}px "JetBrains Mono", monospace`;
  ctx.fillText(url, ctaX, ctaY + 12);

  ctx.fillStyle = MUTED;
  ctx.font = '500 24px "JetBrains Mono", monospace';
  ctx.fillText(`sportshubconnect.com  |  ${sport.toUpperCase()}`, 72, SIZE - 76);

  return canvas;
};

export const canvasBlob = canvas => new Promise(resolve => canvas.toBlob(resolve, 'image/png'));

export const downloadBlob = (blob, filename) => {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
};

export const canShareFile = () => typeof navigator.canShare === 'function' && typeof navigator.share === 'function';

export const shareBlob = async (blob, filename, title) => {
  const file = new File([blob], filename, { type: 'image/png' });
  if (!canShareFile() || !navigator.canShare({ files: [file] })) return false;
  await navigator.share({ files: [file], title });
  return true;
};
